import { useId, useState } from "react";

import { ApiError } from "../../api/client";
import { errorMessage, useRequestAccess } from "../../api/queries";
import type { AccessGrant, Purpose } from "../../api/types";
import { PURPOSES } from "../../api/types";
import { formatBytes, formatSeconds } from "../../lib/format";
import { PURPOSE_LABELS } from "../../lib/metrics";
import { CopyBlock } from "../ui/CopyBlock";
import { Dialog } from "../ui/Dialog";
import { Field } from "../ui/Field";

interface AccessDialogProps {
  datasetId: string;
  versionNumber: number;
  filename: string;
  /** What the current consent covers, so the picker offers only those. */
  permitted: Purpose[];
  onClose: () => void;
}

/**
 * The refusal, in the server's own sentence and nothing else.
 *
 * `403 purpose_not_permitted` names the client, when the consent was recorded
 * and what the evidence was, which purpose was refused, and what to do about
 * it. Every one of those is a fact this browser does not hold, so the message
 * is printed exactly as it arrived — a paraphrase here would be the console
 * saying something the console did not check.
 */
function Refusal({ error }: { error: ApiError }): JSX.Element {
  return (
    <div className="refusal" role="alert">
      <p className="refusal__message">{error.message}</p>
      <p className="refusal__meta mono">
        {error.status} · {error.code}
      </p>
    </div>
  );
}

/** The link, once, with what it is for and what it is not. */
function Granted({ grant }: { grant: AccessGrant }): JSX.Element {
  return (
    <>
      <p className="dialog__text">
        Here is a link to {grant.filename} — {formatBytes(grant.size_bytes)},
        good for {formatSeconds(grant.expires_in)}. It is for this one file and
        nothing else.
      </p>
      <CopyBlock value={grant.url} label="Copy the link" />
      <p className="dialog__text">
        Check what you download against this, which is what the console measured
        on these exact bytes:
      </p>
      <p className="mono dialog__checksum">{grant.checksum_sha256}</p>
      <p className="form__note">
        Nothing revokes this once it is in a terminal; it stops working when it
        expires. The console recorded that it was issued, and to whom — it does
        not see the transfer, so it will never say whether the file moved.
      </p>
    </>
  );
}

/**
 * Ask for a file, naming why.
 *
 * There is no purposeless read in this product: every request declares one of
 * the three words, and the console checks it against what the client allows
 * right now — not what they allowed when the file arrived. A request that is
 * refused is a row in the ledger like any other, and the *Who used this*
 * screen shows both halves.
 *
 * Args:
 *     datasetId: The dataset.
 *     versionNumber: The version to read.
 *     filename: What the file is called, for the sentence.
 *     permitted: The purposes the current consent covers.
 *     onClose: Close the dialog.
 *
 * Returns:
 *     The dialog.
 */
export function AccessDialog({
  datasetId,
  versionNumber,
  filename,
  permitted,
  onClose,
}: AccessDialogProps): JSX.Element {
  const purposeId = useId();
  const noteId = useId();
  const [purpose, setPurpose] = useState<Purpose | "">("");
  const [note, setNote] = useState("");
  const request = useRequestAccess(datasetId, versionNumber);

  const refusal =
    request.error instanceof ApiError && request.error.isPurposeNotPermitted
      ? request.error
      : null;

  return (
    <Dialog
      title={`Get ${filename}`}
      onClose={onClose}
      footer={
        request.data ? (
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={request.isPending || purpose === ""}
              onClick={() =>
                request.mutate({
                  purpose: purpose as Purpose,
                  note: note.trim() ? note.trim() : null,
                })
              }
            >
              {request.isPending ? "Asking…" : "Get the link"}
            </button>
          </>
        )
      }
    >
      {request.data ? (
        <Granted grant={request.data} />
      ) : (
        <>
          <Field
            label="Why are you reading this?"
            htmlFor={purposeId}
            hint="The console will not guess why you are reading a client's data."
          >
            <select
              id={purposeId}
              className="select"
              value={purpose}
              onChange={(event) => setPurpose(event.target.value as Purpose | "")}
            >
              <option value="">Pick one</option>
              {PURPOSES.map((option) => (
                <option
                  key={option}
                  value={option}
                  disabled={!permitted.includes(option)}
                >
                  {PURPOSE_LABELS[option]}
                  {permitted.includes(option) ? "" : " — not consented"}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="A note, for whoever reads the ledger later"
            htmlFor={noteId}
            hint="Optional, one line."
          >
            <input
              id={noteId}
              className="input"
              value={note}
              maxLength={200}
              onChange={(event) => setNote(event.target.value)}
              placeholder="churn benchmark for the August review"
            />
          </Field>

          {refusal ? (
            <Refusal error={refusal} />
          ) : request.isError ? (
            <p className="form__error" role="alert">
              {errorMessage(request.error)}
            </p>
          ) : null}
        </>
      )}
    </Dialog>
  );
}
